import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { MsalService } from "@azure/msal-angular";
import { Subscription } from "rxjs";
import { NavigationEnd, NavigationStart, Router, RoutesRecognized } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { SecurityService } from './services/security-service/security.service';
import { MonitoringService } from './services/monitor.service';
import { ConfigurationSettings } from './configuration-settings';
import { environment } from '../environments/environment';
import { UserModel } from './user/user.model';
import { MenuModel } from './menu/menu.model';
import { MenuService } from './services/menu-service/menu.service';
import { LoadingService } from './loading/res-loading.service';
import { CommonService } from './services/common-service/common.service';
import { PrimeNGConfig } from 'primeng/api';
import { MsalBroadcastService } from "@azure/msal-angular";
import { MessageService } from "primeng/api";
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, pairwise, takeUntil } from 'rxjs/operators';
import * as signalR from "@microsoft/signalr";


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [SecurityService, MenuService, CommonService, MessageService]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  private subscription: Subscription;
  public username: string;
  public userModel: UserModel;
  public showMenuOnAuthentication: object = { 'visibility': 'hidden' };

  public cartItemsCount: number = 0;
  public appVersion: string = environment.appVersion;

  growlMsgs: ToastModule[] = [];
  currentEnvironment = ConfigurationSettings.CURRENT_ENVIRONMENT_NAME;
  isDisabled: true;

  skipSearchPage: boolean = false;

  public showMenu: boolean = false;
  public menus: MenuModel[] = [];
  adminMenus: MenuModel[];
  nonAdminMenus: MenuModel[];
  supportMenus: MenuModel[];
  public exists: boolean = false;
  public isRequester: boolean = false;
  public additionalRoutes = ['request-types/Stock/request', 'request-types/GLP/request', 'request-types/P2P/request']
  private readonly _destroying$ = new Subject<void>();
  
  // Add loading state management to prevent race conditions
  private isMenusLoading: boolean = false;
  private isMenusLoaded: boolean = false;
  private pendingNavigation: string | null = null;
  public connection: signalR.HubConnection;
  public theMessage: string;
  public displayMaintenanceMessage: boolean = false;


  public previousRouteURL = '';
  constructor(private broadcastService: MsalBroadcastService, 
              private authService: MsalService,
              private router: Router, 
              private securityService: SecurityService,
              private monitoringService: MonitoringService,
              private primengConfig: PrimeNGConfig, 
              private messageService: MessageService,
              private loadingService: LoadingService, 
              private menuService: MenuService, 
              private commonService: CommonService) {
    //    this.router.navigate(['404']);
    this.router.events.subscribe((event) => {
      if (((event instanceof NavigationStart)
        // && event.url != '/unauthorized'
        && event.url != '/home'
        // && event.url != '/search'
        && event.url != '/cart'
        && event.url != '/code'
        && event.url != '/access-denied')
        || ((event instanceof NavigationEnd) && event.urlAfterRedirects == '/search')
      ) {

        // Code to clear all local storage and Session Storage on logout click.
        if (event.url == "/after-logout") {
          this.menus = [];
          this.showMenu = false;
          this.isMenusLoaded = false;
          this.isMenusLoading = false;
          localStorage.clear();
          this.router.navigate(["./search"]);
        }

        // Handle navigation with proper race condition protection
        this.handleNavigation(event.url);
      }
    });
  }

  //----------------------------------------------------------------------
  // Handle navigation with proper race condition protection
  //----------------------------------------------------------------------
  private handleNavigation(url: string): void {
    console.log(`Navigation requested to: ${url}, menus loaded: ${this.isMenusLoaded}, menus loading: ${this.isMenusLoading}`);
    
    // Load menus from localStorage if available
    if (localStorage.getItem("msal.menus") != null && localStorage.getItem("msal.menus") != "" && !this.isMenusLoaded) {
      this.getMenusFromlocalStorage();
    }

    // For local development, bypass permission checks
    if (ConfigurationSettings.CURRENT_ENVIRONMENT_NAME === 'LOCAL') {
      if (url === "/") {
        this.router.navigate(["./search"]);
      }
      // Skip permission checks for local development
      localStorage["msal.user.validated"] = "true";
      return;
    }

    // If menus are currently being loaded, store the pending navigation
    if (this.isMenusLoading) {
      console.log(`Menus are loading, storing pending navigation: ${url}`);
      this.pendingNavigation = url;
      return;
    }

    // If menus are loaded, proceed with normal permission checking
    if (this.isMenusLoaded && this.menus != null && this.menus.length > 0) {
      this.checkNavigationPermission(url);
    } else {
      // Menus not loaded yet, handle based on authentication state
      this.handleNavigationWithoutMenus(url);
    }
  }

  //----------------------------------------------------------------------
  // Check navigation permission when menus are available
  //----------------------------------------------------------------------
  private checkNavigationPermission(url: string): void {
    if (url === "/") {
      this.router.navigate(["./search"]);
      return;
    }

    // Improved permission checking with better URL matching
    const cleanUrl = url.replace(/^\/+/, ''); // Remove leading slashes
    const urlParts = cleanUrl.split('/');
    const basePath = urlParts[0];

    // Check if the route exists in menus with exact or partial matching
    this.exists = this.menus.some(menu => {
      const menuUrl = menu.ActionURL;
      return cleanUrl === menuUrl || 
             cleanUrl.startsWith(menuUrl + '/') ||
             basePath === menuUrl;
    }) || this.additionalRoutes.some(route => {
      return cleanUrl === route || cleanUrl.startsWith(route + '/');
    });

    console.log(`Permission check for ${url}: ${this.exists ? 'ALLOWED' : 'DENIED'}`);
    
    if (!this.exists) {
      console.log(`Redirecting to access-denied. Available menus:`, this.menus.map(m => m.ActionURL));
      this.router.navigate(["./access-denied"]);
    }
  }

  //----------------------------------------------------------------------
  // Handle navigation when menus are not yet loaded
  //----------------------------------------------------------------------
  private handleNavigationWithoutMenus(url: string): void {
    console.log(`Handling navigation without menus for: ${url}`);
    
    if (localStorage["msal.user.validated"] == "true") {
      // User is validated but menus not loaded yet, redirect to home temporarily
      console.log("User validated, redirecting to home while menus load");
      this.router.navigate(["./home"]);
    } else if (localStorage["msal.user.validated"] == undefined) {
      // Authentication state unclear, redirect to home
      console.log("Authentication state unclear, redirecting to home");
      this.router.navigate(["./home"]);
    } else {
      // User explicitly not validated
      console.log("User not validated, redirecting to access-denied");
      this.router.navigate(["./access-denied"]);
    }
  }

  //----------------------------------------------------------------------
  // Process any pending navigation after menus are loaded
  //----------------------------------------------------------------------
  private processPendingNavigation(): void {
    if (this.pendingNavigation) {
      console.log(`Processing pending navigation: ${this.pendingNavigation}`);
      const url = this.pendingNavigation;
      this.pendingNavigation = null;
      this.checkNavigationPermission(url);
    }
  }

  ngOnInit() {
    // This if case is handling page refresh after login success. 
    // msal.user.validated (user define storage)-> Prefix name msal is given because it was not clear by logout.
    // once msal is given this storage is cleaned by logout. 
    //  this.router.navigate(['404']);
    console.log('ngOnInit');

    this.primengConfig.ripple = true;
    this.initSignalR();
    let currentUrl = window.location.href;
    if (currentUrl.indexOf('step') > 0 || (currentUrl.indexOf('/request-types/') > 0 && currentUrl.indexOf('/request/') > 0)) {
      this.skipSearchPage = true;
    }
    this.broadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        filter(() => { return this.authService.instance.getAllAccounts().length > 0; }),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (localStorage["msal.user.validated"] === undefined || localStorage["msal.user.validated"] === null || localStorage["msal.user.validated"] === 'false') {
          this.username = this.authService.instance.getActiveAccount()?.username;
          localStorage.setItem("loggedInUsername", this.username);
          if (!(this.username == undefined || this.username == "guest" || this.username == "")) {
            this.validateUser();
          }
          else {
            console.log("username is either undefined or guest");
            localStorage["msal.user.validated"] = "false";
            this.showMenuOnAuthentication = { 'visibility': 'hidden' };
          }
        }
      });

    if (localStorage.getItem("msal.account.keys")) {
      this.getMenus();
      this.showMenuOnAuthentication = { 'visibility': 'visible' };
      if (localStorage.getItem("msal.role") != null) {
        this.isRequester = this.commonService.checkRequester();
      }
    }
    else {
      // this.broadcastService.subscribe("msal:loginFailure", () => {
      //   localStorage["msal.user.validated"] = "false";
      //   this.showMenuOnAuthentication = { 'visibility': 'hidden' };
      // });
    }

    if (localStorage.getItem("msal.menus") != null && localStorage.getItem("msal.menus") != "") {
      this.getMenusFromlocalStorage();
    }
    else {
      this.getMenus();
    }

    if (localStorage["msal.user.validated"] == "true") {
      this.setPreviousRouteURL();
    }

  }
  public ngAfterViewInit(): void {
    const el = document.querySelector("res-app-settings-options res-navigation-group");
    el.removeAttribute("hidden");
  }
  //-------------------------------------------
  //To set the previous URL when route change.
  //-------------------------------------------
  public setPreviousRouteURL() {

    this.router.events
      .pipe(filter((e: any) => e instanceof RoutesRecognized),
        pairwise()
      ).subscribe((e: any) => {
        this.previousRouteURL = e[0].urlAfterRedirects;
        console.log(e[0].urlAfterRedirects); // previous url
      });
  }

  public initSignalR() {
    this.connection = new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.Information)
      .withUrl(ConfigurationSettings.CurrentEnvironment.ApiPath + "/hub", {
        withCredentials: false // Fix for CORS issue
      })
      .build();

    this.connection.start().then(function () {
      console.log('SignalR Connected!');
    });

    this.connection.on("SendGreeting", (message: string) => {
      this.theMessage = message;
      this.displayMaintenanceMessage = true;
    });
    this.connection.on("NewUserWelcome", (message: string) => {
      this.messageService.add({ severity: 'success', summary: '', detail: message });
    });
  }

  public closeMaintenanceMessage() {
    this.theMessage = "";
    this.displayMaintenanceMessage = false;
  }

  ngOnDestroy() {
    localStorage.removeItem("loggedInUsername");
    localStorage.removeItem("loggedInUserRoles");
    localStorage["msal.user.validated"] = "false";
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  //--------------------------------------------
  // Method to redirect to Cart Page.
  //--------------------------------------------
  public showCartDetailsPage() {
    this.router.navigateByUrl("/cart");
  }

  //----------------------------------------------------------------------
  // Check the user in database and show the menu bar if available in it.
  //----------------------------------------------------------------------
  public validateUser() {
    console.log("Validating User : " + this.username);
    localStorage.setItem("loggedInUsername", this.username);

    // For local development, bypass user validation
    if (ConfigurationSettings.CURRENT_ENVIRONMENT_NAME === 'LOCAL') {
      console.log("Local development - bypassing user validation");
      this.showMenuOnAuthentication = { 'visibility': 'visible' };
      localStorage["msal.user.validated"] = "true";
      localStorage["msal.displayName"] = this.username || 'Local Developer';
      
      // Create dummy menus for local development
      this.menus = [
        { MenuId: 1, ParentMenuId: null, Name: 'Home', ActionURL: 'home', DisplayOrder: 1 },
        { MenuId: 2, ParentMenuId: null, Name: 'Search', ActionURL: 'search', DisplayOrder: 2 },
        { MenuId: 3, ParentMenuId: null, Name: 'Cart', ActionURL: 'cart', DisplayOrder: 3 }
      ];
      
      localStorage.setItem("msal.menus", JSON.stringify(this.menus));
      this.getGroupedMenus();
      this.showMenu = true;
      this.isMenusLoaded = true;
      this.isMenusLoading = false;
      
      // Process any pending navigation after menus are set
      this.processPendingNavigation();
      
      if (!this.skipSearchPage) {
        this.router.navigate(["search"]);
      }
      return;
    }

    // Normal validation for non-local environments
    this.securityService.validateUser(this.username)
      .subscribe(data => this.userModel = data,
        error => {
          console.log("Error in Validating User : " + error);
          this.showMenuOnAuthentication = { 'visibility': 'hidden' };
          // This session storage is used to show the menu itme when user refresh the page.
          // false means hide the menu. 
          localStorage["msal.user.validated"] = "false";

          this.menus = [];
          localStorage.removeItem('msal.menus');
          this.showMenu = false;
          this.isMenusLoaded = false;
          this.isMenusLoading = false;
          this.router.navigate(['./access-denied']);
          //this.authService.loginRedirect();
          //          if (window.location.host.toLowerCase().indexOf('login') < 0)
          //            this.router.navigate(['./login']);
        },
        async () => {
          var result = <boolean>await this.getMenus();

          if (result) {
            localStorage["msal.userId"] = this.userModel.UserId;
            localStorage.setItem("msal.role", JSON.stringify(this.userModel.UserRequestTypeRoleList));
            localStorage["msal.displayName"] = this.userModel.FirstName + ' ' + this.userModel.LastName;
            console.log("Current Logged in Username : " + this.username);
            this.showMenuOnAuthentication = { 'visibility': 'visible' };

            // This session storage is used to show the menu itme when user refresh the page.
            // true means show the menu. 
            localStorage["msal.user.validated"] = "true";
            localStorage.removeItem("gsscommon_token_acquired");  // invalidate gss common token
            this.isRequester = this.commonService.checkRequester();
            if (!this.skipSearchPage) {
              this.router.navigate(["search"]);
            }
          } else {
            // Menu loading failed, but user is validated - redirect to home
            console.warn("User validated but menu loading failed, redirecting to home");
            localStorage["msal.user.validated"] = "true";
            this.router.navigate(["home"]);
          }
        });
  }

  public getMenus() {
    console.log('Starting menu loading...');
    this.isMenusLoading = true;
    this.isMenusLoaded = false;
    
    return new Promise((resolve) => {
      this.menuService.getMenuByEmail(localStorage.getItem("loggedInUsername"))
        .subscribe(data => {
          this.menus = data;
          console.log('Menus received from API:', data?.length || 0, 'items');
        },
          error => {
            console.error('Error loading menus:', error);
            this.showMenu = false;
            this.showMenuOnAuthentication = { 'visibility': 'hidden' }
            this.loadingService.clearMessage();
            this.isMenusLoading = false;
            this.isMenusLoaded = false;
            //this.authService.loginRedirect();
            resolve(false);
          },
          () => {
            this.isMenusLoading = false;
            
            if (this.menus.length > 0) {
              this.getGroupedMenus();
              localStorage.setItem("msal.menus", JSON.stringify(this.menus));
              this.isMenusLoaded = true;
              console.log('Menus successfully loaded and cached');
              
              // Process any pending navigation after menus are loaded
              this.processPendingNavigation();
            } else {
              console.warn('No menus received for user');
              this.isMenusLoaded = false;
            }
            
            this.loadingService.clearMessage();
            resolve(this.menus.length > 0);
          });
    });
  }

  public getMenusFromlocalStorage() {
    console.log('Loading menus from localStorage...');
    try {
      this.menus = JSON.parse(localStorage.getItem("msal.menus")) as MenuModel[];
      
      if (this.menus && this.menus.length > 0) {
        this.getGroupedMenus();
        this.isMenusLoaded = true;
        this.isMenusLoading = false;
        console.log('Menus loaded from localStorage:', this.menus.length, 'items');
        
        // Process any pending navigation after menus are loaded
        this.processPendingNavigation();
      } else {
        console.warn('No valid menus found in localStorage');
        this.isMenusLoaded = false;
      }
    } catch (error) {
      console.error('Error parsing menus from localStorage:', error);
      localStorage.removeItem("msal.menus"); // Clear corrupted data
      this.isMenusLoaded = false;
      this.isMenusLoading = false;
    }
  }

  private getGroupedMenus() {
    var adminMenu = this.menus.find(x => x.Name == 'Administration');
    if (adminMenu != undefined) {
      this.adminMenus = this.menus.filter(x => x.MenuId == adminMenu.MenuId || x.ParentMenuId == adminMenu.MenuId);
    }
    var supportMenu = this.menus.find(x => x.Name == 'Support');
    if (supportMenu != undefined) {
      this.supportMenus = this.menus.filter(x => x.MenuId == supportMenu.MenuId || x.ParentMenuId == supportMenu.MenuId);
    }

    if (adminMenu != undefined && supportMenu != undefined) {
      this.nonAdminMenus = this.menus.filter(x => x.MenuId != adminMenu.MenuId && x.ParentMenuId != adminMenu.MenuId
        && x.MenuId != supportMenu.MenuId && x.ParentMenuId != supportMenu.MenuId);
    }
    else if (adminMenu != undefined) {
      this.nonAdminMenus = this.menus.filter(x => x.MenuId != adminMenu.MenuId && x.ParentMenuId != adminMenu.MenuId);
    }
    else if (supportMenu != undefined) {
      this.nonAdminMenus = this.menus.filter(x => x.MenuId != supportMenu.MenuId && x.ParentMenuId != supportMenu.MenuId);
    }
  }

}
